import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
    getStats,
    getLeads,
    getContactMessages,
    getAdminToken,
    setAdminToken,
    clearAdminToken,
} from "@/lib/api";
import { Lock, LogOut, Download, RefreshCw, Users, Mail, MessageSquare, Search as SearchIcon, Eye, EyeOff } from "lucide-react";

// ---------- CSV export helper ----------
const toCSV = (rows, columns) => {
    const escape = (v) => {
        if (v == null) return "";
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const header = columns.map((c) => escape(c.label)).join(",");
    const body = rows.map((r) => columns.map((c) => escape(c.get(r))).join(",")).join("\n");
    return `${header}\n${body}`;
};
const downloadCSV = (filename, csv) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    link.click();
    URL.revokeObjectURL(url);
};
const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
};

// ---------- Login card ----------
function AdminLogin({ onLogin }) {
    const [token, setToken] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!token.trim()) return;
        setLoading(true);
        setAdminToken(token.trim());
        try {
            await getStats();
            toast.success("Signed in.");
            onLogin();
        } catch (err) {
            clearAdminToken();
            if (err?.response?.status === 401) toast.error("Invalid admin token.");
            else if (err?.response?.status === 503) toast.error("Admin access is not configured on the server.");
            else toast.error("Couldn't verify token. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="min-h-[70vh] grid place-items-center container-editorial py-16">
            <form
                onSubmit={submit}
                data-testid="admin-login-form"
                className="w-full max-w-md bg-white border border-stone-200 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]"
            >
                <div className="w-12 h-12 rounded-full bg-primary/10 text-primary grid place-items-center">
                    <Lock className="h-5 w-5" />
                </div>
                <h1 className="font-serif text-3xl mt-6">Admin sign-in</h1>
                <p className="text-stone-600 mt-2 text-sm">
                    Enter the admin token from your backend <code className="font-mono text-xs bg-stone-100 px-1.5 py-0.5 rounded">.env</code> to view leads, subscribers and messages.
                </p>
                <label className="text-xs uppercase tracking-widest text-stone-500 mt-8 block">Admin token</label>
                <div className="relative mt-1">
                    <Input
                        data-testid="admin-token-input"
                        type={show ? "text" : "password"}
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        placeholder="paste ADMIN_TOKEN here"
                        className="pr-10 h-11 font-mono text-sm"
                        autoFocus
                        required
                    />
                    <button
                        type="button"
                        onClick={() => setShow((v) => !v)}
                        aria-label="Toggle visibility"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-stone-500 hover:text-stone-800"
                    >
                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                </div>
                <Button
                    data-testid="admin-login-submit"
                    disabled={loading}
                    className="w-full mt-6 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-11"
                >
                    {loading ? "Verifying…" : "Sign in"}
                </Button>
                <p className="text-[11px] text-stone-500 mt-4">
                    Rate-limited. Token is stored in your browser only. Sign out clears it instantly.
                </p>
            </form>
        </section>
    );
}

// ---------- Stat card ----------
function StatCard({ icon: Icon, label, value, tint }) {
    return (
        <Card className={`p-6 border-stone-200 ${tint || ""}`}>
            <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-stone-500">{label}</span>
                <Icon className="h-5 w-5 text-primary" />
            </div>
            <p className="font-serif text-4xl mt-3">{value}</p>
        </Card>
    );
}

// ---------- Filter helper ----------
const filterRows = (rows, q, keys) => {
    const s = (q || "").trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => keys.some((k) => (r[k] || "").toString().toLowerCase().includes(s)));
};

// ---------- Dashboard ----------
function AdminDashboard({ onLogout }) {
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [s, l, m] = await Promise.all([getStats(), getLeads(1000), getContactMessages(1000)]);
            setStats(s);
            setLeads(l);
            setMessages(m);
        } catch (err) {
            if (err?.response?.status === 401) {
                clearAdminToken();
                toast.error("Session expired. Please sign in again.");
                onLogout();
                return;
            }
            toast.error("Failed to load admin data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line

    const ebookLeads = useMemo(() => leads.filter((l) => l.source !== "newsletter"), [leads]);
    const newsletterLeads = useMemo(() => leads.filter((l) => l.source === "newsletter"), [leads]);

    const filteredEbook = filterRows(ebookLeads, q, ["email", "name", "source", "ebook_slug"]);
    const filteredNewsletter = filterRows(newsletterLeads, q, ["email"]);
    const filteredMessages = filterRows(messages, q, ["email", "name", "subject", "message"]);

    const exportLeads = () => downloadCSV(
        `smart-hustle-leads-${new Date().toISOString().slice(0, 10)}.csv`,
        toCSV(ebookLeads, [
            { label: "Created at", get: (r) => r.created_at },
            { label: "Email", get: (r) => r.email },
            { label: "Name", get: (r) => r.name || "" },
            { label: "Source", get: (r) => r.source },
            { label: "eBook", get: (r) => r.ebook_slug || "" },
        ])
    );
    const exportNewsletter = () => downloadCSV(
        `smart-hustle-newsletter-${new Date().toISOString().slice(0, 10)}.csv`,
        toCSV(newsletterLeads, [
            { label: "Created at", get: (r) => r.created_at },
            { label: "Email", get: (r) => r.email },
        ])
    );
    const exportMessages = () => downloadCSV(
        `smart-hustle-messages-${new Date().toISOString().slice(0, 10)}.csv`,
        toCSV(messages, [
            { label: "Created at", get: (r) => r.created_at },
            { label: "Name", get: (r) => r.name },
            { label: "Email", get: (r) => r.email },
            { label: "Subject", get: (r) => r.subject || "" },
            { label: "Message", get: (r) => r.message },
        ])
    );

    return (
        <section className="container-editorial py-16" data-testid="admin-dashboard">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <p className="label-eyebrow text-primary">Admin</p>
                    <h1 className="font-serif text-4xl md:text-5xl mt-2 leading-tight">Dashboard</h1>
                    <p className="text-stone-600 mt-2">Leads, subscribers and messages from your public forms.</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        data-testid="admin-refresh"
                        onClick={load}
                        variant="outline"
                        className="rounded-full h-10 px-5 border-stone-300"
                        disabled={loading}
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                    <Button
                        data-testid="admin-logout"
                        onClick={onLogout}
                        variant="outline"
                        className="rounded-full h-10 px-5 border-stone-300"
                    >
                        <LogOut className="h-4 w-4 mr-2" /> Sign out
                    </Button>
                </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-5 mt-10">
                <StatCard icon={Users} label="Ebook Leads" value={stats?.leads != null ? stats.leads - (stats.newsletter_subscribers || 0) : "—"} />
                <StatCard icon={Mail} label="Newsletter" value={stats?.newsletter_subscribers ?? "—"} />
                <StatCard icon={MessageSquare} label="Contact Messages" value={stats?.contact_messages ?? "—"} />
            </div>

            <div className="mt-10 relative max-w-md">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                    data-testid="admin-search"
                    className="pl-9 h-11"
                    placeholder="Search email, name, subject…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
            </div>

            <Tabs defaultValue="ebook" className="mt-8" data-testid="admin-tabs">
                <TabsList className="bg-stone-100">
                    <TabsTrigger data-testid="admin-tab-ebook" value="ebook">Ebook Leads ({filteredEbook.length})</TabsTrigger>
                    <TabsTrigger data-testid="admin-tab-newsletter" value="newsletter">Newsletter ({filteredNewsletter.length})</TabsTrigger>
                    <TabsTrigger data-testid="admin-tab-messages" value="messages">Messages ({filteredMessages.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="ebook">
                    <div className="flex justify-end mt-6">
                        <Button data-testid="admin-export-leads" onClick={exportLeads} className="rounded-full bg-primary hover:bg-primary/90 h-10 px-5 text-primary-foreground">
                            <Download className="h-4 w-4 mr-2" /> Export CSV
                        </Button>
                    </div>
                    <div className="mt-4 overflow-x-auto border border-stone-200 rounded-lg bg-white">
                        <table className="w-full text-sm">
                            <thead className="bg-stone-50 text-left text-xs uppercase tracking-widest text-stone-500">
                                <tr>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Email</th>
                                    <th className="px-4 py-3">Name</th>
                                    <th className="px-4 py-3">Source</th>
                                    <th className="px-4 py-3">eBook</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {filteredEbook.length === 0 && (
                                    <tr><td colSpan="5" className="px-4 py-10 text-center text-stone-500">No leads yet.</td></tr>
                                )}
                                {filteredEbook.map((l) => (
                                    <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                                        <td className="px-4 py-3 whitespace-nowrap">{fmt(l.created_at)}</td>
                                        <td className="px-4 py-3 font-medium">{l.email}</td>
                                        <td className="px-4 py-3">{l.name || "—"}</td>
                                        <td className="px-4 py-3"><Badge className="bg-primary/10 text-primary hover:bg-primary/10 border-0">{l.source}</Badge></td>
                                        <td className="px-4 py-3 text-stone-600">{l.ebook_slug || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="newsletter">
                    <div className="flex justify-end mt-6">
                        <Button data-testid="admin-export-newsletter" onClick={exportNewsletter} className="rounded-full bg-primary hover:bg-primary/90 h-10 px-5 text-primary-foreground">
                            <Download className="h-4 w-4 mr-2" /> Export CSV
                        </Button>
                    </div>
                    <div className="mt-4 overflow-x-auto border border-stone-200 rounded-lg bg-white">
                        <table className="w-full text-sm">
                            <thead className="bg-stone-50 text-left text-xs uppercase tracking-widest text-stone-500">
                                <tr>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Email</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {filteredNewsletter.length === 0 && (
                                    <tr><td colSpan="2" className="px-4 py-10 text-center text-stone-500">No subscribers yet.</td></tr>
                                )}
                                {filteredNewsletter.map((l) => (
                                    <tr key={l.id} data-testid={`news-row-${l.id}`}>
                                        <td className="px-4 py-3 whitespace-nowrap">{fmt(l.created_at)}</td>
                                        <td className="px-4 py-3 font-medium">{l.email}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="messages">
                    <div className="flex justify-end mt-6">
                        <Button data-testid="admin-export-messages" onClick={exportMessages} className="rounded-full bg-primary hover:bg-primary/90 h-10 px-5 text-primary-foreground">
                            <Download className="h-4 w-4 mr-2" /> Export CSV
                        </Button>
                    </div>
                    <div className="mt-4 space-y-3">
                        {filteredMessages.length === 0 && (
                            <p className="text-center text-stone-500 py-10">No messages yet.</p>
                        )}
                        {filteredMessages.map((m) => (
                            <div key={m.id} data-testid={`msg-row-${m.id}`} className="bg-white border border-stone-200 rounded-lg p-5">
                                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-stone-500">
                                    <span>{fmt(m.created_at)}</span>
                                    <a href={`mailto:${m.email}`} className="text-primary hover:underline">{m.email}</a>
                                </div>
                                <p className="font-serif text-lg mt-2 leading-tight">{m.subject || "(no subject)"}</p>
                                <p className="text-sm text-stone-500 mt-1">From <span className="font-medium text-stone-800">{m.name}</span></p>
                                <p className="text-stone-700 mt-3 whitespace-pre-wrap">{m.message}</p>
                            </div>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>
        </section>
    );
}

// ---------- Wrapper ----------
export default function Admin() {
    const [authed, setAuthed] = useState(!!getAdminToken());
    const navigate = useNavigate();
    useEffect(() => {
        // If token exists, we still let dashboard verify on first fetch.
    }, []);
    const logout = () => {
        clearAdminToken();
        setAuthed(false);
        toast.success("Signed out.");
    };
    return authed ? <AdminDashboard onLogout={logout} /> : <AdminLogin onLogin={() => setAuthed(true)} />;
}
